import type { Cart } from "@workspace/crystallize/generated/shop/cart/";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useOptimistic,
	useState,
	useTransition,
} from "react";
import { useFetcher } from "react-router";

type CartResponse =
	| {
			success?: boolean;
			cart?: Cart;
			error?: string;
	  }
	| Cart;

type Extra = {
	sku: string;
	quantity: number;
};

type CartContextProps = {
	cart: Cart | null;
	isLoading: boolean;
	isOpen: boolean;
	emptyCart: () => void;
	setIsOpen: (isOpen: boolean) => void;
	updateCart: (
		sku: string,
		quantity: number,
		action: "update" | "remove",
		openCartAfterUpdate?: boolean,
		extras?: Extra[],
	) => void;
	toggleCart: () => void;
};

const CartContext = createContext<CartContextProps | undefined>(undefined);

type CartProviderProps = {
	children: React.ReactNode;
};

export const CartProvider = ({ children }: CartProviderProps) => {
	const fetcher = useFetcher<CartResponse>();
	const [isOpen, setIsOpen] = useState(false);
	const [isPending, startTransition] = useTransition();
	const [cart, setCart] = useState<Cart | null>(null);

	const [optimisticCart, addOptimisticCart] = useOptimistic<Cart | null, Cart>(
		cart,
		(prevCart, newCart) => newCart,
	);

	useEffect(() => {
		fetch("/api/cart").then(async (res) => {
			if (res.ok && res.status !== 204) {
				const cart = (await res.json()) as Cart;
				setCart(cart);
			}
		});
	}, []);

	const toggleCart = useCallback(() => {
		if (!isOpen && !cart) {
			startTransition(() => {
				fetcher.load("/api/cart");
			});
		}
		setIsOpen(!isOpen);
	}, [isOpen, cart, fetcher]);

	const updateCart = (
		sku: string,
		quantity: number,
		action: "update" | "remove",
		openCartAfterUpdate = false,
		extras?: Extra[],
	) => {
		if (cart !== null) {
			const updatedItems = cart.items
				.map((item) => {
					if (item.variant.sku === sku) {
						const newQty =
							action === "update"
								? item.quantity + quantity
								: Math.max(0, item.quantity - quantity);
						return { ...item, quantity: newQty };
					}
					return item;
				})
				.filter((item) => item.quantity > 0);

			const gross = updatedItems.reduce(
				(total, item) => total + item.price.gross,
				0,
			);

			const optimistic = {
				...cart,
				items: updatedItems,
				total: {
					...cart.total,
					gross,
				},
			};

			startTransition(() => {
				addOptimisticCart(optimistic);
			});
		}

		startTransition(() => {
			fetcher.submit(
				{
					sku,
					quantity: quantity.toString(),
					action,
					...(extras && { extras: JSON.stringify(extras) }),
				},
				{ method: "POST", action: "/api/cart" },
			);
		});

		if (openCartAfterUpdate) {
			setIsOpen(true);
		}
	};

	// Handle fetcher data updates
	if (fetcher.data && !isPending) {
		const data = fetcher.data;
		const newCart =
			"cart" in data && data.success
				? data.cart
				: "items" in data
					? (data as Cart)
					: null;

		if (newCart && JSON.stringify(newCart) !== JSON.stringify(cart)) {
			startTransition(() => {
				setCart(newCart);
			});
		}
	}

	const emptyCart = () => {
		setCart({ items: [], total: { gross: 0 } });
	};

	const value: CartContextProps = {
		cart: optimisticCart,
		isLoading: isPending || fetcher.state !== "idle",
		isOpen,
		emptyCart,
		setIsOpen,
		updateCart,
		toggleCart,
	};

	return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
	const context = useContext(CartContext);
	if (context === undefined) {
		throw new Error("useCart must be used within a CartProvider");
	}
	return context;
};
